import { useTrips } from 'tripPlanner/hooks/api/trip'
import useChatTripPlannerReleaseState from 'tripPlanner/hooks/useChatTripPlannerReleaseState'
import { useWishlistIndex } from 'wishlists/hooks'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'

interface WishlistsAndChatDecision {
  isLoggedIn: boolean
  isEligible: boolean
  hasWishlistsOrChats: boolean
  hasClassicTripPlannerTrips: boolean
  isLoading: boolean
  hasError: boolean
}

export function shouldUseWishlists({
  isLoggedIn,
  isEligible,
  hasWishlistsOrChats,
  hasClassicTripPlannerTrips,
  isLoading,
  hasError,
}: WishlistsAndChatDecision): boolean {
  const isBlockedByClassicTrips = hasClassicTripPlannerTrips && !hasWishlistsOrChats
  return (
    isLoggedIn &&
    isEligible &&
    !isBlockedByClassicTrips &&
    !isLoading &&
    !hasError
  )
}

interface WishlistsAndChatReleaseState {
  shouldUseWishlists: boolean
  shouldUseChat: boolean
  isEligible: boolean
  isLoading: boolean
  isError: boolean
}

export default function useWishlistsAndChatReleaseState(): WishlistsAndChatReleaseState {
  const {
    isLoggedIn,
    isChatTripPlannerReleaseEnabled,
    hasChatTripPlannerChats,
    hasClassicTripPlannerTrips,
    isLoadingChatTripPlannerDirectEntryDecision,
    hasChatTripPlannerDirectEntryDecisionError,
  } = useChatTripPlannerReleaseState()

  // Existing wishlist items should still override classic trips when there are no chats,
  // even for users already in the release cohort.
  const shouldCheckForExistingWishlists =
    isLoggedIn && !hasChatTripPlannerChats
  const { data: wishlistData, isLoading: isLoadingWishlists } = useWishlistIndex({
    enabled: shouldCheckForExistingWishlists,
  })
  const hasExistingWishlistItems = Boolean(
    wishlistData?.wishlists.some((wishlist) => wishlist.itemCount > 0),
  )
  const isCheckingWishlists = shouldCheckForExistingWishlists && isLoadingWishlists

  const hasWishlistsOrChats = hasChatTripPlannerChats || hasExistingWishlistItems
  const isEligible = isChatTripPlannerReleaseEnabled || hasWishlistsOrChats

  // Verified trips check: forces a real fetch to avoid SSR cache false positives.
  // useTrips has placeholderData: [] which can satisfy the query from SSR cache
  // without a real fetch. Override placeholderData and staleTime for certainty.
  const {
    data: verifiedTrips = [],
    isSuccess: tripsVerified,
    isFetching: tripsVerifying,
  } = useTrips({
    enabled: isLoggedIn && isEligible,
    placeholderData: undefined,
    staleTime: 0,
  })

  const hasVerifiedTrips = tripsVerified ? verifiedTrips.length > 0 : false
  const isVerifying = isLoggedIn && isEligible && !tripsVerified && tripsVerifying

  const combinedLoading =
    isLoadingChatTripPlannerDirectEntryDecision || isVerifying || isCheckingWishlists

  const isMobileOrTablet = useIsMobileOrTablet()

  const useWishlists = shouldUseWishlists({
    isLoggedIn,
    isEligible,
    hasWishlistsOrChats,
    hasClassicTripPlannerTrips: hasClassicTripPlannerTrips || hasVerifiedTrips,
    isLoading: combinedLoading,
    hasError: hasChatTripPlannerDirectEntryDecisionError,
  })

  return {
    shouldUseWishlists: useWishlists,
    shouldUseChat: useWishlists && !isMobileOrTablet,
    isEligible,
    isLoading: combinedLoading,
    isError: hasChatTripPlannerDirectEntryDecisionError,
  }
}
